import React from 'react'
import { cn } from '../../utils/cn'
import { useFormControlContext } from './form-control'

const inputSizes: Record<string, string> = {
  sm: 'h-[2.1rem] px-3 text-sm',
  md: 'h-9 px-3 text-sm',
  lg: 'h-10 px-4 text-base',
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: keyof typeof inputSizes
  isInvalid?: boolean
  isDisabled?: boolean
  variant?: 'outline' | 'filled' | 'unstyled'
}

export const baseFieldClass =
  'w-full rounded-md border bg-surface-section text-content-primary placeholder:text-content-placeholder transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 aria-disabled:cursor-not-allowed aria-disabled:opacity-60'

/**
 * Emulates the native `disabled` behaviour (blocks focus, pointer, keyboard and
 * edits) WITHOUT the native `disabled` attribute, so the disabled state is driven
 * by our own JS/CSS and stays agnostic of per-browser defaults. Exposes the state
 * to assistive tech via `aria-disabled` and removes the control from the tab order.
 *
 * Spread these AFTER the element's own props/handlers so the guards win while
 * disabled; when enabled it returns nothing and the element behaves normally.
 * Pair with the `aria-disabled:` utility classes for the muted look.
 */
export function disabledFieldProps(isDisabled?: boolean) {
  if (!isDisabled) return {}
  const swallow = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }
  return {
    'aria-disabled': true,
    tabIndex: -1,
    onMouseDownCapture: swallow,
    onPointerDownCapture: swallow,
    onClickCapture: swallow,
    onKeyDownCapture: swallow,
    onChange: swallow,
    onInput: swallow,
    onPaste: swallow,
  }
}

/**
 * Canonical border treatment for field controls. Every input-like primitive
 * (Input, Textarea, Select, NumberInput, CountrySelect, PhoneInput, …) must use
 * these so the valid/invalid look is identical across the design system.
 */
export const fieldValidClass = 'border-line-subtle hover:border-line-emphasized'
export const fieldInvalidClass = 'border-danger focus-visible:ring-danger/30'
export const fieldStateClass = (invalid?: boolean) =>
  invalid ? fieldInvalidClass : fieldValidClass

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputSize = 'sm',
      isInvalid,
      isDisabled,
      variant = 'outline',
      disabled,
      required,
      id,
      ...props
    },
    ref,
  ) => {
    // Inherit id/invalid/required/disabled state from a surrounding FormControl
    // so the label (htmlFor) and this input share an id and are properly
    // associated, matching our FormControl behavior.
    const ctx = useFormControlContext()
    const resolvedId = id ?? ctx.id
    const invalid = isInvalid ?? ctx.isInvalid
    const isRequired = required ?? ctx.isRequired
    const isDisabledResolved = disabled || isDisabled || ctx.isDisabled
    return (
      <input
        ref={ref}
        id={resolvedId}
        // Intentionally NOT setting the native `required` attribute: validation is
        // react-hook-form driven (with custom inline messages). A native `required`
        // makes the browser's constraint validation block form submission before
        // RHF's onSubmit runs, so its error messages never surface. Expose the
        // requirement to assistive tech via `aria-required` only.
        aria-required={isRequired || undefined}
        aria-invalid={invalid || undefined}
        className={cn(
          baseFieldClass,
          inputSizes[inputSize],
          variant === 'filled' ? 'bg-surface-subtle' : 'bg-surface-section',
          fieldStateClass(invalid),
          className,
        )}
        {...props}
        // Disabled state is driven by JS/CSS (aria-disabled + guards), not the
        // native `disabled` attribute — spread last so guards win while disabled.
        {...disabledFieldProps(isDisabledResolved)}
      />
    )
  },
)
Input.displayName = 'Input'

export const InputGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('relative flex items-center', className)} {...props} />
  ),
)
InputGroup.displayName = 'InputGroup'

export const InputLeftElement = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'pointer-events-none absolute left-0 top-0 flex h-full w-9 items-center justify-center text-content-muted',
      className,
    )}
    {...props}
  />
))
InputLeftElement.displayName = 'InputLeftElement'

export const InputRightElement = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'absolute right-0 top-0 flex h-full w-9 items-center justify-center text-content-muted',
      className,
    )}
    {...props}
  />
))
InputRightElement.displayName = 'InputRightElement'

export const InputLeftAddon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center rounded-l-md border border-r-0 border-line-subtle bg-surface-subtle px-3 text-sm text-content-secondary',
      className,
    )}
    {...props}
  />
))
InputLeftAddon.displayName = 'InputLeftAddon'

export const InputRightAddon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center rounded-r-md border border-l-0 border-line-subtle bg-surface-subtle px-3 text-sm text-content-secondary',
      className,
    )}
    {...props}
  />
))
InputRightAddon.displayName = 'InputRightAddon'
